<template>
  <div v-if="policyDetails">
    <RevContainer class="py-12 md:my-24">
      <RevLink
        class="body-2-bold flex items-center gap-8"
        :to="{ name: ROUTE_NAMES.DASHBOARD.SERVICES_HUB }"
        :tracking="{
          zone: TRACKING_ZONES.POLICY_DETAILS,
          name: 'go-back-to-all-policies',
        }"
      >
        <IconArrowLeft />
        {{ i18n(translations.backLabel) }}
      </RevLink>
    </RevContainer>

    <div class="flex justify-center pb-72 md:mx-32 md:gap-32">
      <div class="max-w-[650px] grow space-y-16 md:space-y-32">
        <RevContainer
          class="bg-surface-default-low py-24 md:rounded-lg md:overflow-hidden md:py-32"
        >
          <div class="space-y-20 md:space-y-24">
            <div>
              <PolicyStateTag :state="policyDetails.policy.state" />
              <PolicyAndOrderlineDetails
                :orderline="policyDetails.orderline"
                :policy="policyDetails.policy"
              />
            </div>
            <SubmitClaim
              v-if="displaySubmitClaim"
              class="pt-4 md:hidden"
              :partner-name="policyDetails.policy.offer.partnerName"
              :policy-state="policyDetails.policy.state"
            />
            <CoverageDetails :benefits="policyDetails.policy.offer.benefits" />
          </div>
        </RevContainer>

        <RevContainer
          class="bg-surface-default-low py-24 md:rounded-lg md:overflow-hidden md:py-32"
          ><div class="space-y-24 md:space-y-32">
            <PaymentDetails
              :due-amount="policyDetails.dueAmount"
              :next-payment="policyDetails.nextPayment"
              :policy-id
              :state="policyDetails.policy.state"
            />
            <PaymentHistory :payment-history="policyDetails.paymentHistory" />
          </div>
        </RevContainer>

        <div class="px-24 pt-16 md:hidden">
          <HelpAndCancelButtons
            :orderline="policyDetails.orderline"
            :policy="policyDetails.policy"
          />
        </div>
      </div>

      <!-- Only on desktop -->
      <div class="hidden min-w-[438px] space-y-32 md:block">
        <div
          v-if="displaySubmitClaim"
          class="bg-surface-default-low rounded-lg space-y-16 overflow-hidden p-32"
        >
          <div class="heading-3">
            {{ i18n(translations.submitClaimSectionTitle) }}
          </div>
          <SubmitClaim
            :partner-name="policyDetails.policy.offer.partnerName"
            :policy-state="policyDetails.policy.state"
          />
        </div>

        <div
          class="bg-surface-default-low rounded-lg space-y-16 overflow-hidden p-32"
        >
          <div class="heading-3">
            {{ i18n(translations.helpSectionTitle) }}
          </div>
          <HelpAndCancelButtons
            :orderline="policyDetails.orderline"
            :policy="policyDetails.policy"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRouteParams } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevContainer } from '@ds/components/Container'
import { RevLink } from '@ds/components/Link'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'

import { ROUTE_NAMES } from '@/scopes/dashboard/constants'

import PolicyStateTag from '../../components/PolicyStateTag/PolicyStateTag.vue'
import { TRACKING_ZONES } from '../../constant'

import translations from './PolicyDetails.translations'
import CoverageDetails from './components/CoverageDetails/CoverageDetails.vue'
import HelpAndCancelButtons from './components/HelpAndCancelButtons/HelpAndCancelButtons.vue'
import PaymentDetails from './components/PaymentDetails/PaymentDetails.vue'
import PaymentHistory from './components/PaymentHistory/PaymentHistory.vue'
import PolicyAndOrderlineDetails from './components/PolicyAndOrderlineDetails/PolicyAndOrderlineDetails.vue'
import SubmitClaim from './components/SubmitClaim/SubmitClaim.vue'

const { policyId } = useRouteParams()
const $httpFetch = useHttpFetch()
const i18n = useI18n()

const { data: policyDetails } = await useAsyncData(() =>
  $httpFetch('/api/insurance/v1/services-hub/policies/:policyId', {
    method: 'GET',
    pathParams: { policyId },
  }),
)

const displaySubmitClaim = computed(() => {
  return (
    policyDetails.value?.policy.state === 'ACTIVE' ||
    policyDetails.value?.policy.state === 'ON_HOLD'
  )
})
</script>
